import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createBuyer,
  getBuyers,
  updateBuyer,
  deleteBuyer,
  getBuyerDetail,
  buyerMetrices,
} from "../api/ApiRoutesFile";
import { baseURL } from "../api/axiosInstance";

export const buyerApiSlice = createApi({
  reducerPath: "buyerApi",
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Buyer"],
  endpoints: (builder) => ({
    getBuyers: builder.query({
      query: ({ endpoint, lastId, params }) => {
        // Handle the new endpoint format: users/all?type=customer
        let url = endpoint;
        
        // Parse existing query parameters from endpoint
        const [baseUrl, existingParams] = endpoint.split('?');
        const searchParams = new URLSearchParams(existingParams || '');
        
        // Add search parameter if provided
        if (params) {
          // Extract search value from params string (format: "search=value")
          const searchMatch = params.match(/search=([^&]*)/);
          if (searchMatch && searchMatch[1]) {
            const searchValue = decodeURIComponent(searchMatch[1]);
            if (searchValue.trim()) {
              searchParams.set('search', searchValue.trim());
            }
          }
        }
        
        // Reconstruct URL with all parameters
        const finalParams = searchParams.toString();
        url = finalParams ? `${baseUrl}?${finalParams}` : baseUrl;
        
        console.log("Buyer API Query Debug:");
        console.log("- endpoint:", endpoint);
        console.log("- params:", params);
        console.log("- final url:", url);
        
        return {
          url,
          method: "GET",
        };
      },
      // Disable caching for search results to ensure fresh data
      keepUnusedDataFor: 0, // Don't cache search results
      refetchOnMountOrArgChange: true, // Always refetch when arguments change
      refetchOnFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: false, // Don't refetch on network reconnection
      providesTags: (result, error, { params }) => {
        const baseTags = [{ type: "Buyer", id: "LIST" }];
        
        if (result) {
          const users = result.users || result.data?.users || [];
          const userTags = users.map(({ _id }) => ({
            type: "Buyer",
            id: _id,
          }));
          
          // Add search-specific tags to ensure proper cache invalidation
          const searchTag = params ? { type: "Buyer", id: `SEARCH-${params}` } : null;
          
          return [
            ...userTags,
            ...baseTags,
            ...(searchTag ? [searchTag] : []),
          ];
        }
        
        return baseTags;
      },
    }),
    getBuyerDetail: builder.query({
      query: (id) => ({
        url: `${getBuyerDetail}/${id}`,
        method: "GET",
      }),
      providesTags: ["Buyer"],
    }),
    createBuyer: builder.mutation({
      query: (formData) => ({
        url: createBuyer,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Buyer"],
    }),
    updateBuyer: builder.mutation({
      query: ({ id, formData }) => ({
        url: `${updateBuyer}${id}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: ["Buyer"],
    }),
    deleteBuyer: builder.mutation({
      query: (id) => ({
        url: `${deleteBuyer}${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Buyer"],
    }),
    getBuyerMetrices: builder.query({
      query: () => ({
        url: buyerMetrices,
        method: "GET",
      }),
      providesTags: ["Buyer"],
    }),
  }),
});

export const {
  useGetBuyersQuery,
  useGetBuyerDetailQuery,
  useCreateBuyerMutation,
  useUpdateBuyerMutation,
  useDeleteBuyerMutation,
  useGetBuyerMetricesQuery,
} = buyerApiSlice;